import {Box3, Mesh, PlaneGeometry, Quaternion, Raycaster, Vector3} from "three";
import {createGroup,findChildrenByNames} from "../../utils/threeUtils";
import {buildingGroup, mousePosition, tmpGroup} from "../../main";
import * as Globals from "../../globals";
import {clearGroup} from "../../sceneManager";
import {setObjectLayer} from "../../utils/layerUtils";
import {Measurements} from "./measurements";
import {getWallOrientation} from "../walls/wall";
import {toFixedFloat} from "../../utils/utils";
import * as nameContants from "../../constants/nameConstants";
import {pillarMeshName, raehmMeshName, supportBeamMeshName} from "../../constants/nameConstants";
import {lineMaterial, measurementsMaterial} from "../../constants/materialConstants";

let raehmRaycaster = new Raycaster();
raehmRaycaster.params.Mesh.threshold = 0.05;
raehmRaycaster.layers.enable(Globals.layers.raehm);
raehmRaycaster.layers.enable(Globals.layers.supportBeam);

const endLineLength = 0.1;
const lineWidth = 0.01;

let lastLiveMeasurementPoints = [];
let lastOrientation = null;
let liveMeasurementGroup = null;
var allElements = [];

export function generateLiveMeasurement(data) {
    clearLiveMeasurementState();
    try {
        if (data === null || data.object === null || !['beam', nameContants.wallGroupName].includes(data.type)) {
            return;
        }

        let wall = data.object;
        let wallPosition = new Box3().setFromObject(wall).max;
        wallPosition.y -= 0.1;
        let orientation = getWallOrientation(wall);
        if (!allElements.length) {
            allElements = findChildrenByNames(buildingGroup, [raehmMeshName, supportBeamMeshName, pillarMeshName] , /*recursive*/true);
        }
        let invertedOrientation = orientation === "x" ? "z" : "x";
        let buildingBounds = new Box3().setFromObject(buildingGroup);
        let raycasterValues = getRaycasterValues(buildingBounds, invertedOrientation, wallPosition);
        raehmRaycaster.set(raycasterValues.origin, raycasterValues.direction);
        let intersections1 = raehmRaycaster.intersectObjects(allElements, false); //Schnittpunkte Richtung 1 mit den Wänden
        raehmRaycaster.set(raycasterValues.invertedOrigin, raycasterValues.invertedDirection);
        let intersections2 = raehmRaycaster.intersectObjects(allElements, false); //Schnittpunkte Richtung 2 mit den Wänden

        if (intersections1.length !== intersections2.length) {
            console.error("Keine eindeutige Anzahl an Elementen für Raycaster");
            return;
        }

        if (intersections1.length === 0) {
            console.warn("Live Bemaßung konnte nicht erstellt werden keine Intersections gefunden.");
            return;
        }

        intersections2 = intersections2.reverse();

        let sortedIntersections = [];
        for (let i = 0; i < intersections1.length; i++) {
            sortedIntersections.push({
                object: intersections1[i].object,
                start: intersections1[i].point,
                end: intersections2[i].point
            });
        }

        //TODO bei L wand 1x3 und eingefügten Durchbruch kann anschließend keine Bemaßung mehr erstellt werden und alle weiteren Funktionen werden blockiert
        let startPoint = sortedIntersections[0].start;
        let endPoint = sortedIntersections[sortedIntersections.length - 1].end;
        liveMeasurementGroup = liveMeasurementGroup || createGroup("liveMeasurement");
        let geometry = new PlaneGeometry(new Vector3().subVectors(endPoint, startPoint).length(), 0.01);
        geometry.rotateX(-Math.PI / 2);
        let line = new Mesh(geometry, lineMaterial);
        line.name = "measurementLine";
        let center = new Vector3().copy(startPoint).add(new Vector3().subVectors(endPoint, startPoint).multiplyScalar(0.5));
        if (orientation === "x") {
            line.rotation.y = Math.PI / 2;
        }
        liveMeasurementGroup.position.set(center.x, 4, center.z);
        liveMeasurementGroup.addWithUuid(line);

        let measurementPoints = [];
        for (let i = 0; i < sortedIntersections.length; i++) {
            let start = sortedIntersections[i].start;
            let end = sortedIntersections[i].end;
            measurementPoints.push(start);
            measurementPoints.push(end);

            generateSectionForLiveMeasurement(liveMeasurementGroup, start, end, orientation, i === sortedIntersections.length - 1);

            if (i < sortedIntersections.length - 1) {
                //Generiere Bemaßung für leere Fläche zwischen Objekten. Außer bei letztem Objekt danach keine Fläche mehr
                start = sortedIntersections[i].end;
                end = sortedIntersections[i + 1].start;
                measurementPoints.push(start);
                measurementPoints.push(end);
                generateSectionForLiveMeasurement(liveMeasurementGroup, start, end, orientation, false);
            }
        }

        //TODO: Nur überschreiben wenn sich distanzen geändert haben
        if (lastLiveMeasurementPoints.length > 0 && measurementPoints.length === lastLiveMeasurementPoints.length && orientation === lastOrientation) {
            let isSame = false;
            if (orientation === "x") {
                for (let i = 0; i < lastLiveMeasurementPoints.length; i++) {
                    let lastPoint = lastLiveMeasurementPoints[i];
                    let currentPoint = measurementPoints[i];

                    if (toFixedFloat(lastPoint.z) !== toFixedFloat(currentPoint.z)) {
                        break;
                    }
                }
                isSame = true;
            } else {
                for (let i = 0; i < lastLiveMeasurementPoints.length; i++) {
                    let lastPoint = lastLiveMeasurementPoints[i];
                    let currentPoint = measurementPoints[i];

                    if (toFixedFloat(lastPoint.x) !== toFixedFloat(currentPoint.x)) {
                        break;
                    }
                }

                isSame = true;
            }

            if (isSame) {
                lastLiveMeasurementPoints = measurementPoints;
                lastOrientation = orientation;
                if (orientation === "x") {
                    liveMeasurementGroup.position.setX(mousePosition.x);
                } else {
                    liveMeasurementGroup.position.setZ(mousePosition.z);
                }
                return;
            }
        }

        lastLiveMeasurementPoints = measurementPoints;
        lastOrientation = orientation;
        tmpGroup.addWithUuid(liveMeasurementGroup);
        setObjectLayer(liveMeasurementGroup, Globals.layers.layoutViewOnly, true);
    } catch (e) {
        console.error(e);
        clearGroup(tmpGroup);
    }
}

function generateSectionForLiveMeasurement(measurementGroup, start, end, orientation, lastElement) {
    let groupOffset = orientation === "z" ? measurementGroup.position.x : measurementGroup.position.z;

    let measurementArrowLeftGeometry = new PlaneGeometry(endLineLength, lineWidth);
    measurementArrowLeftGeometry.computeBoundingBox();
    let measurementArrowLeft = new Mesh(measurementArrowLeftGeometry, measurementsMaterial);
    measurementArrowLeft.name = "measurementArrow";
    measurementArrowLeft.quaternion.setFromAxisAngle(new Vector3(1, 0, 0), Math.PI / 2);
    let quaternion = new Quaternion();
    quaternion.setFromAxisAngle(new Vector3(0, 1, 0), Math.PI / 4);
    measurementArrowLeft.applyQuaternion(quaternion);
    if (orientation === "x") {
        measurementArrowLeft.position.set(0, 4, start.z - groupOffset);
    } else {
        measurementArrowLeft.position.set(start.x - groupOffset, 4, 0);
    }

    let diff = new Vector3().subVectors(end, start);

    if (diff.length() < 0.1) {
        return;
    }
    let midPosition = new Vector3().copy(start).add(new Vector3().copy(diff).multiplyScalar(0.5));
    midPosition.y = 4;
    if (orientation === "x") {
        midPosition.x = 0.05;
        midPosition.z -= groupOffset;
    } else {
        midPosition.z = 0.05;
        midPosition.x -= groupOffset;
    }
    let widthTextField = Measurements.createMeasurementText(diff.length(), midPosition);

    if (orientation === "x") {
        widthTextField.rotation.z = Math.PI / 2;
    }

    measurementGroup.addWithUuid(widthTextField);

    let measurementArrowRight = null;
    if (lastElement) {
        measurementArrowRight = measurementArrowLeft.clone();
        if (orientation === "x") {
            measurementArrowRight.position.set(0, 4, end.z - groupOffset);
        } else {
            measurementArrowRight.position.set(end.x - groupOffset, 4, 0);
        }
        measurementGroup.addWithUuid(measurementArrowRight);
    }

    measurementGroup.addWithUuid(measurementArrowLeft);

    return [widthTextField, measurementArrowLeft, measurementArrowRight];
}

function getRaycasterValues(buildingBounds, orientation, wallPosition) {
    let origin, direction, invertedOrigin, invertedDirection;
    if (orientation === "x") {
        origin = new Vector3(buildingBounds.min.x, wallPosition.y, mousePosition.z);
        invertedOrigin = new Vector3(buildingBounds.max.x, wallPosition.y, mousePosition.z);
        direction = new Vector3(1, 0, 0);
        invertedDirection = new Vector3(-1, 0, 0);
    } else {
        origin = new Vector3(mousePosition.x, wallPosition.y, buildingBounds.min.z);
        invertedOrigin = new Vector3(mousePosition.x, wallPosition.y, buildingBounds.max.z);
        direction = new Vector3(0, 0, 1);
        invertedDirection = new Vector3(0, 0, -1);
    }

    return {
        origin: origin,
        direction: direction,
        invertedOrigin: invertedOrigin,
        invertedDirection: invertedDirection
    };
}

//Bereinigen
export function clearLiveMeasurementState() {
    lastLiveMeasurementPoints = [];
    lastOrientation = null;
    allElements = [];
    if (liveMeasurementGroup !== null) {
        clearGroup(liveMeasurementGroup);
        liveMeasurementGroup.removeFromParent();
    }
    liveMeasurementGroup = null;
}
